import { ReactNode } from 'react'

// Assistant replies are markdown: the analytics context answers ranked questions
// with GFM tables, so rendering them as text is unreadable. Hand-rolled over a
// markdown library to keep the http(s)-only anchor guarantee and avoid
// dangerouslySetInnerHTML — model output is never trusted as HTML.

// GFM allows a table with or without the outer pipes, so a row is any line
// containing one. Both the row and its divider must, which keeps a setext
// heading ("Product" over "---") from being read as a one-column table.
const TABLE_ROW = /\|/
const TABLE_DIVIDER = /^[\s:|-]*\|[\s:|-]*$/
const HEADING = /^\s{0,3}(#{1,6})\s+(.*)$/
const BULLET = /^\s*[-*]\s+(.*)$/
const ORDERED = /^\s*\d+[.)]\s+(.*)$/
const FENCE = /^\s*```/
const QUOTE = /^\s*>\s?(.*)$/
const RULE = /^\s{0,3}([-*_])\1{2,}\s*$/

// Precedence matters: code first so backticks are never re-parsed, then links
// (their label must not be eaten by emphasis), then bold before italic so `**`
// never matches as a pair of `*`. Single-underscore italic is deliberately
// unsupported: it turns `ecommerce_group_id` in a reply into mangled emphasis.
const INLINE =
  /`([^`]+)`|\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*/g

const ALIGNMENT: Record<string, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center'
}

export function renderMarkdown(content: string): ReactNode {
  return <>{renderBlocks(content.split('\n'))}</>
}

function renderBlocks(lines: string[]): ReactNode[] {
  const blocks: ReactNode[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (line.trim() === '') {
      i++
      continue
    }

    // Before the table check: a fence's contents are opaque and must never be
    // parsed as markdown, and its rows can legitimately contain pipes.
    if (FENCE.test(line)) {
      i++
      const code: string[] = []
      while (i < lines.length && !FENCE.test(lines[i])) {
        code.push(lines[i])
        i++
      }
      i++ // the closing fence, or past the end when the model never emitted one
      blocks.push(
        <pre
          key={blocks.length}
          className="overflow-x-auto rounded bg-white/10 px-2 py-1.5 font-mono text-xs"
        >
          {code.join('\n')}
        </pre>
      )
      continue
    }

    if (TABLE_ROW.test(line) && TABLE_DIVIDER.test(lines[i + 1] ?? '')) {
      const header = splitRow(line)
      const align = splitRow(lines[i + 1]).map(alignmentOf)
      i += 2
      const rows: string[][] = []
      while (i < lines.length && TABLE_ROW.test(lines[i])) {
        rows.push(splitRow(lines[i]))
        i++
      }
      blocks.push(
        <Table key={blocks.length} header={header} align={align} rows={rows} />
      )
      continue
    }

    // After the table check, so a `--- | ---` divider is never taken for a rule.
    if (RULE.test(line)) {
      blocks.push(<hr key={blocks.length} className="border-white/10" />)
      i++
      continue
    }

    if (QUOTE.test(line)) {
      const quoted: string[] = []
      while (i < lines.length) {
        const q = QUOTE.exec(lines[i])
        if (!q) break
        quoted.push(q[1])
        i++
      }
      blocks.push(
        <blockquote
          key={blocks.length}
          className="border-l-2 border-white/20 pl-3 text-white/70"
        >
          {renderInline(quoted.join(' '))}
        </blockquote>
      )
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      blocks.push(
        <p key={blocks.length} className="font-semibold text-white">
          {renderInline(heading[2])}
        </p>
      )
      i++
      continue
    }

    if (BULLET.test(line) || ORDERED.test(line)) {
      const ordered = !BULLET.test(line)
      const pattern = ordered ? ORDERED : BULLET
      const items: string[] = []
      while (i < lines.length) {
        const item = pattern.exec(lines[i])
        if (!item) break
        items.push(item[1])
        i++
      }
      blocks.push(<List key={blocks.length} ordered={ordered} items={items} />)
      continue
    }

    // A paragraph runs to the next blank line or block start; markdown joins its
    // lines with a space rather than keeping the source wrapping.
    const paragraph: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !startsBlock(lines, i)
    ) {
      paragraph.push(lines[i].trim())
      i++
    }
    blocks.push(<p key={blocks.length}>{renderInline(paragraph.join(' '))}</p>)
  }
  return blocks
}

function renderInline(text: string): ReactNode[] {
  const parts: ReactNode[] = []
  let cursor = 0
  for (const match of text.matchAll(INLINE)) {
    const [full, code, label, href, bold, boldAlt, italic] = match
    if (match.index > cursor) parts.push(text.slice(cursor, match.index))
    const key = match.index
    if (code !== undefined) {
      parts.push(
        <code
          key={key}
          className="rounded bg-white/10 px-1 py-0.5 font-mono text-[0.85em]"
        >
          {code}
        </code>
      )
    } else if (href !== undefined) {
      parts.push(
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium underline underline-offset-2 hover:opacity-80"
        >
          {label}
        </a>
      )
    } else if (bold !== undefined || boldAlt !== undefined) {
      parts.push(
        <strong key={key} className="font-semibold text-white">
          {renderInline(bold ?? boldAlt)}
        </strong>
      )
    } else {
      parts.push(<em key={key}>{renderInline(italic)}</em>)
    }
    cursor = key + full.length
  }
  parts.push(text.slice(cursor))
  return parts
}

function Table({
  header,
  align,
  rows
}: {
  header: string[]
  align: string[]
  rows: string[][]
}) {
  return (
    // The docked panel is resizable but the overlay is capped at 400px, so a wide
    // table scrolls inside the bubble instead of widening it.
    <div className="-mx-1 overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            {header.map((cell, index) => (
              <th
                key={index}
                className={`whitespace-nowrap border-b border-white/15 px-2 py-1.5 font-semibold text-white/60 ${
                  align[index] ?? ALIGNMENT.left
                }`}
              >
                {renderInline(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, index) => (
                <td
                  key={index}
                  className={`border-b border-white/5 px-2 py-1.5 align-top ${
                    align[index] ?? ALIGNMENT.left
                  }`}
                >
                  {renderInline(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function List({ ordered, items }: { ordered: boolean; items: string[] }) {
  const className = `ml-4 flex flex-col gap-0.5 ${
    ordered ? 'list-decimal' : 'list-disc'
  }`
  const children = items.map((item, index) => (
    <li key={index}>{renderInline(item)}</li>
  ))
  return ordered ? (
    <ol className={className}>{children}</ol>
  ) : (
    <ul className={className}>{children}</ul>
  )
}

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((cell) => cell.trim())
}

function alignmentOf(cell: string): string {
  if (cell.startsWith(':') && cell.endsWith(':')) return ALIGNMENT.center
  if (cell.endsWith(':')) return ALIGNMENT.right
  return ALIGNMENT.left
}

// A paragraph must stop when the next line opens another block, otherwise a table
// or list written straight after prose is swallowed into it.
function startsBlock(lines: string[], index: number): boolean {
  const line = lines[index]
  if (
    HEADING.test(line) ||
    BULLET.test(line) ||
    ORDERED.test(line) ||
    FENCE.test(line) ||
    QUOTE.test(line) ||
    RULE.test(line)
  )
    return true
  return TABLE_ROW.test(line) && TABLE_DIVIDER.test(lines[index + 1] ?? '')
}
